import { useState } from 'react'
import { User, ChevronDown, Check } from 'lucide-react'

interface Persona {
  id: string
  name: string
  description: string
  model: string
  temperature: number
  contextWindow: number
  specialties: string[]
}

function PersonaSelector() {
  const personas: Persona[] = [
    {
      id: 'general',
      name: 'General Assistant',
      description: 'Balanced responses for everyday tasks',
      model: 'kimi-k2:1t-cloud',
      temperature: 0.7,
      contextWindow: 128000,
      specialties: ['conversation', 'information', 'analysis']
    },
    {
      id: 'expert',
      name: 'Expert Analyst',
      description: 'Deep analysis and complex problem solving',
      model: 'deepseek-v3.1:671b-cloud',
      temperature: 0.3,
      contextWindow: 128000,
      specialties: ['analysis', 'research', 'strategy']
    },
    {
      id: 'creative',
      name: 'Creative Writer',
      description: 'Imaginative and expressive responses',
      model: 'gpt-oss:120b-cloud',
      temperature: 0.9,
      contextWindow: 128000,
      specialties: ['writing', 'storytelling', 'ideas']
    },
    {
      id: 'coding',
      name: 'Code Specialist',
      description: 'Programming and technical assistance',
      model: 'deepseek-coder:6.7b-instruct-q4_0',
      temperature: 0.2,
      contextWindow: 32000,
      specialties: ['coding', 'debugging', 'architecture']
    },
    {
      id: 'fast',
      name: 'Quick Responder',
      description: 'Fast responses for simple queries',
      model: 'llama3:8b-instruct-q4_0',
      temperature: 0.5,
      contextWindow: 8000,
      specialties: ['quick', 'efficient', 'concise']
    }
  ]

  const [isOpen, setIsOpen] = useState(false)
  const [selectedPersona, setSelectedPersona] = useState<Persona>(personas[0])

  const handlePersonaSelect = (persona: Persona) => {
    setSelectedPersona(persona)
    setIsOpen(false)
    // In a real implementation, this would update the AI model configuration
    console.log('Persona selected:', persona)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-secondary-700 hover:bg-secondary-600 rounded-lg transition-colors"
      >
        <User className="w-4 h-4" />
        <span className="text-sm font-medium">{selectedPersona.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-secondary-800 border border-secondary-700 rounded-lg shadow-lg z-10">
          <div className="p-4 border-b border-secondary-700">
            <h3 className="font-semibold text-secondary-200">Select AI Persona</h3>
            <p className="text-sm text-secondary-400">Choose how your AI assistant behaves</p>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {personas.map((persona) => (
              <button
                key={persona.id}
                onClick={() => handlePersonaSelect(persona)}
                className={`w-full text-left p-4 hover:bg-secondary-700 transition-colors ${
                  selectedPersona.id === persona.id ? 'bg-secondary-700' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {selectedPersona.id === persona.id && (
                      <Check className="w-4 h-4 text-accent-400" />
                    )}
                    <div>
                      <div className="font-medium text-secondary-200">{persona.name}</div>
                      <div className="text-sm text-secondary-400">{persona.description}</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-secondary-500">
                  <span>Model: {persona.model}</span>
                  <span>Temp: {persona.temperature}</span>
                </div>
                
                <div className="flex flex-wrap gap-1 mt-2">
                  {persona.specialties.map((specialty, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-accent-500/20 text-accent-400 rounded text-xs"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>

          <div className="p-4 border-t border-secondary-700 bg-secondary-900/50">
            <div className="text-xs text-secondary-400">
              <div className="flex justify-between mb-1">
                <span>Current Model:</span>
                <span className="text-accent-400">{selectedPersona.model}</span>
              </div>
              <div className="flex justify-between">
                <span>Context Window:</span>
                <span>{selectedPersona.contextWindow.toLocaleString()} tokens</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PersonaSelector
